"use client";

import React, { useEffect, useState, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Paper,
  alpha,
} from "@mui/material";
import {
  Menu as MenuIcon,
  AccountCircle as AccountIcon,
  School as SchoolIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import AppSideBar from "./AppSideBar";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { CLASS_OPTIONS_ARRAY } from "components/enum/ClassOptions";
import { SESSION_OPTIONS_ARRAY } from "components/enum/SessionOptions";
import { useTranslation } from "react-i18next";
import { useSchools } from "../../hooks/useSchools";
import { useTeacher } from "hooks/useTeacher";
import { useStudents, ApplicationStudentDTO } from "hooks/useStudents";
import { useSchoolIdForAdmin } from "hooks/useSchoolIdForAdmin";
import { useClasses } from "../../hooks/useClasses";
import { useSessions } from "../../hooks/useSession";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";
import { format } from "date-fns";

export const DRAWER_WIDTH = 240;

// Medium Context
interface MediumContextType {
  selectedMedium: string;
  setSelectedMedium: (medium: string) => void;
  schoolMediums: string[];
}

const MediumContext = createContext<MediumContextType | undefined>(undefined);

export const useMedium = () => {
  const context = useContext(MediumContext);
  if (!context) {
    throw new Error('useMedium must be used within a MediumProvider');
  }
  return context;
};

interface LayoutProps {
  isAdmin?: boolean;
}

const Layout: React.FC<LayoutProps> = () => {
  const { i18n } = useTranslation();
  const {
    user,
    isLoading: authLoading,
    isTeacher,
    isStudent,
    isSchoolAdmin,
    logout,
    isAdmin,
  } = useAuth();
  // Check if user has APP_USER role
  const isAppUser = user?.roles?.includes('APP_USER');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [studentName, setStudentName] = useState<string>('');
  // Initialize selectedMedium from localStorage
  // For school admin and teacher, always default to "ALL" (ignore localStorage on init)
  // For students, default to empty string (will be set from student data)
  const [selectedMedium, setSelectedMedium] = useState<string>(() => {
    // For school admin and teacher, always start with "ALL" regardless of localStorage
    // This ensures it defaults to "ALL" on refresh and login
    if (isSchoolAdmin() || isTeacher()) {
      return "ALL";
    }
    // For students, try to load from localStorage or default to empty string
    try {
      const stored = localStorage.getItem('selectedMedium');
      if (stored && typeof stored === 'string' && stored.trim() !== '') {
        return stored;
      }
      return "";
    } catch (error) {
      return "";
    }
  });
  const [mediumDropdownOpen, setMediumDropdownOpen] = useState<boolean>(false);

  const {
    selectedSession,
    setSelectedSession,
    selectedClass,
    setSelectedClass,
    clearAllStorage,
  } = useSessionClass();
  const { useSchoolById } = useSchools(authLoading || !user, { enableQuery: false });
  // Disable API call for ADMIN/APP_USER/SCHOOL_ADMIN/TEACHER/STUDENT users - they already have schoolId from user context
  const { data: adminSchoolId, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isAppUser || isAdmin() || isSchoolAdmin() || isTeacher() || isStudent() ? undefined : user?.id
  );

  // For both teachers and school admins, use their schoolId directly from user context
  // This ensures we use the actual schoolId from the logged-in user
  const schoolId = user?.schoolId;

  const { classesQuery } = useClasses(schoolId ?? undefined);
  const { sessionsQuery, activeSessionQuery } = useSessions(schoolId ?? undefined);

  // For TEACHER: fetch staff details by their login email and filter class list accordingly
  const { useStaffByEmail } = useTeacher(undefined, authLoading);
  const staffByEmailQuery = useStaffByEmail(isTeacher() ? user?.email : undefined);

  const { data: school, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
  const { t } = useTranslation();
  // Only call useStudents for non-students (students use useCurrentStudent)
  const { getStudentByStudentCode, updateStudentWithDTOMutation } = useStudents(false, schoolId ?? undefined, !isStudent());
  // Don't fetch all schools for students, school admin, and teachers - we already have schoolId from login
  const { schoolsQuery } = useSchools(authLoading || !user || isStudent() || isSchoolAdmin() || isTeacher(), { enableQuery: !isStudent() && !isSchoolAdmin() && !isTeacher() });

  // For students, use the current student hook to get student data from login
  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();

  // Helper function to get class sort value (numeric part)
  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;

    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

    // Ensure Nursery -> LKG -> UKG always appear before Class 1.
    // (These classes usually don't contain digits, so they'd otherwise go to the end.)
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;

    const numericMatch = className.match(/\d+/);
    if (numericMatch) return parseInt(numericMatch[0], 10);

    return Number.MAX_SAFE_INTEGER;
  };

  // Helper function to get section sort value (A=1, B=2, C=3, etc.)
  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const normalized = section.trim().toUpperCase();
    if (normalized.length === 0) return 0;
    const charCode = normalized.charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      return charCode - 64; // A=1, B=2, C=3, etc.
    }
    return Number.MAX_SAFE_INTEGER;
  };

  // Sort classes: first by numeric class number, then by section letter
  const sortedClassOptions = React.useMemo(() => {
    const classes = classesQuery.data ?? [];
    return [...classes].sort((a: any, b: any) => {
      // First sort by class number (8, 9, 10, etc.)
      const classDiff = getClassSortValue(a.name) - getClassSortValue(b.name);
      if (classDiff !== 0) return classDiff;

      // Then sort by section letter (A, B, C, D, etc.)
      const sectionDiff = getSectionSortValue(a.section) - getSectionSortValue(b.section);
      if (sectionDiff !== 0) return sectionDiff;

      // Final fallback to alphabetical
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [classesQuery.data]);

  const normalizeTeacherClassToken = (value: string) =>
    (value || "")
      .toString()
      .trim()
      // Make teacher strings like "6th A" match class records like { name: "6th", section: "A" }
      // by stripping all non-alphanumeric characters (spaces, dashes, etc.)
      .replace(/[^a-z0-9]/gi, "")
      .toLowerCase();

  const teacherAssignedClasses = React.useMemo(() => {
    if (!isTeacher()) return null;
    const teacherOfClass =
      (staffByEmailQuery.data as any)?.teacherOfClass ??
      (staffByEmailQuery.data as any)?.teacherOfClasses ??
      (staffByEmailQuery.data as any)?.TeacherOfClasses;
    if (!Array.isArray(teacherOfClass) || teacherOfClass.length === 0) return [];

    return teacherOfClass
      .filter(Boolean)
      .map((x: any) => normalizeTeacherClassToken(String(x)))
      .filter((x: string) => x.length > 0);
  }, [isTeacher, staffByEmailQuery.data]);

  // Filter options for students to show only their enrolled class/session/medium
  // Use currentStudentData from login for students
  const studentData = isStudent() ? currentStudentData : null;

  /** Sentinel section for UI-only "3rd-ALL" style entries (not from API). */
  const GRADE_ALL_SECTION = "__GRADE_ALL__";
  /** Sentinel section for UI-only "Class-ALL" entry (not from API). */
  const CLASS_ALL_SECTION = "__CLASS_ALL__";

  const classOptionsWithGradeAll = React.useMemo(() => {
    const base = sortedClassOptions;
    if (!base.length) return base;

    const gradeNames = Array.from(new Set(base.map((c: any) => c.name).filter(Boolean))) as string[];
    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

    const schoolIdFallback = base[0]?.schoolId ?? 0;
    const merged: any[] = [];
    let synthIdx = 0;

    // "Class-ALL" entry: used to fetch across all classes for selected session
    merged.push({
      id: -1_500_000 - synthIdx++,
      name: "Class",
      section: CLASS_ALL_SECTION,
      schoolId: schoolIdFallback,
    });

    for (const grade of gradeNames) {
      merged.push({
        id: -1_000_000 - synthIdx++,
        name: grade,
        section: GRADE_ALL_SECTION,
        schoolId: schoolIdFallback,
      });
      merged.push(...base.filter((c: any) => c.name === grade));
    }
    return merged;
  }, [sortedClassOptions]);

  const teacherFilteredClassOptions = React.useMemo(() => {
    if (!isTeacher()) return null;

    // While loading teacher mapping, don't over-restrict; UI will settle once fetched.
    if (staffByEmailQuery.isLoading || staffByEmailQuery.isFetching) {
      return classOptionsWithGradeAll;
    }

    const tokens = teacherAssignedClasses;
    if (!tokens) return classOptionsWithGradeAll;
    if (tokens.length === 0) return [];

    const isGradeAllToken = (token: string) => token.endsWith("all");

    const exactSet = new Set<string>();
    const gradeAllSet = new Set<string>();

    for (const token of tokens) {
      if (isGradeAllToken(token)) {
        const gradeName = token.replace(/-?all$/i, "").trim();
        if (gradeName) gradeAllSet.add(gradeName);
      } else {
        exactSet.add(token);
      }
    }

    const classKey = (name?: string, section?: string | null) =>
      normalizeTeacherClassToken(`${name || ""}${section || ""}`);

    // Filter out the synthetic "-ALL" entries unless teacher has grade-all assignment for that grade.
    return classOptionsWithGradeAll.filter((cls: any) => {
      if (!cls?.name) return false;

      // Always keep Class-ALL entry available.
      if (cls.section === CLASS_ALL_SECTION) return true;

      // Synthetic grade ALL entries
      if (cls.section === GRADE_ALL_SECTION) {
        const gradeToken = normalizeTeacherClassToken(cls.name);
        return gradeAllSet.has(gradeToken);
      }

      const key = classKey(cls.name, cls.section ?? "");
      if (exactSet.has(key)) return true;

      const gradeToken = normalizeTeacherClassToken(cls.name);
      return gradeAllSet.has(gradeToken);
    });
  }, [
    isTeacher,
    staffByEmailQuery.isLoading,
    staffByEmailQuery.isFetching,
    classOptionsWithGradeAll,
    teacherAssignedClasses,
  ]);

  const classOptions =
    isStudent() && studentData?.schoolClassId
      ? sortedClassOptions.filter((cls: any) => cls.id === studentData.schoolClassId)
      : isTeacher()
        ? (teacherFilteredClassOptions ?? classOptionsWithGradeAll)
        : classOptionsWithGradeAll;

  const sessionData = isStudent() && studentData?.sessionId
    ? (sessionsQuery.data ?? []).filter((ses: any) => ses.id === studentData.sessionId)
    : (sessionsQuery.data ?? []);

  const formatClassOption = (cls?: { name?: string; section?: string | null }) => {
    if (!cls?.name) return "";
    if (cls.section === CLASS_ALL_SECTION) return `Class-ALL`;
    if (cls.section === GRADE_ALL_SECTION) return `${cls.name}-ALL`;
    return cls.section ? `${cls.name} - ${cls.section}` : cls.name;
  };

  const selectedClassValue = selectedClass ? formatClassOption(selectedClass) : "";
  const selectedSessionValue = selectedSession?.name ?? "";

  const currentYear = new Date().getFullYear();
  // Show all sessions (both active and inactive)
  const sessionOptions = sessionData.map((session) => session.name);

  useEffect(() => {
    // Check if selectedSession still exists in the sessions list (not deleted)
    const resolveNextSession = () =>
      activeSessionQuery.data ??
      sessionData.find((s) => s.isActive) ??
      sessionData[sessionData.length - 1];

    const sessionStillExists =
      !!selectedSession &&
      sessionData.some((s) => {
        if (selectedSession.id && s.id) {
          return s.id === selectedSession.id;
        }
        return s.name === selectedSession.name;
      });

    if (selectedSession) {
      const matchingSession = sessionData.find((s) =>
        selectedSession.id && s.id
          ? s.id === selectedSession.id
          : s.name === selectedSession.name
      );
      if (matchingSession) {
        const hasChanged =
          matchingSession.isActive !== selectedSession.isActive ||
          matchingSession.name !== selectedSession.name ||
          matchingSession.updatedAt !== (selectedSession as any)?.updatedAt;
        if (hasChanged) {
          setSelectedSession(matchingSession);
          return;
        }
      }
    }

    if (sessionData.length === 0) {
      // Don't clear selectedSession if we're still loading - it might be restored from localStorage
      if (selectedSession && !sessionsQuery.isLoading) {
        setSelectedSession(null);
      }
      return;
    }

    if (selectedSession && !sessionStillExists) {
      const nextSession = resolveNextSession();
      if (nextSession) {
        setSelectedSession(nextSession);
      } else {
        setSelectedSession(null);
      }
      return;
    }

    // Only set default session if no session is selected AND we're not loading (to avoid overriding localStorage restore)
    if (!selectedSession && !sessionsQuery.isLoading) {
      const nextSession = resolveNextSession();
      if (nextSession) {
        setSelectedSession(nextSession);
      }
    }
  }, [
    sessionData,
    selectedSession,
    setSelectedSession,
    activeSessionQuery.data,
    sessionsQuery.isLoading,
  ]);

  useEffect(() => {

    if (classOptions.length === 0) {
      // Don't clear selectedClass if we're still loading - it might be restored from localStorage
      if (selectedClass && !classesQuery.isLoading) {
        setSelectedClass(null);
      }
      return;
    }

    // Check if stored class exists in current class options
    const classStillExists =
      !!selectedClass &&
      classOptions.some((cls) => {
        // Prefer id match, but also allow matching by (name + section) for synthetic "*-ALL" entries
        // whose IDs may differ across components.
        const idMatch =
          selectedClass.id != null && cls.id != null
            ? cls.id === selectedClass.id
            : false;
        const nameSectionMatch =
          cls.name === selectedClass.name &&
          (cls.section ?? "") === (selectedClass.section ?? "");
        return idMatch || nameSectionMatch;
      });

    // If stored class exists, ensure it's properly set (in case of ID mismatch but same name)
    if (selectedClass && classStillExists) {
      const matchingClass = classOptions.find((cls) => {
        const idMatch =
          selectedClass.id != null && cls.id != null
            ? cls.id === selectedClass.id
            : false;
        const nameSectionMatch =
          cls.name === selectedClass.name &&
          (cls.section ?? "") === (selectedClass.section ?? "");
        return idMatch || nameSectionMatch;
      });

      // Update to the matching class from options to ensure we have the latest data
      if (matchingClass && (matchingClass.id !== selectedClass.id || matchingClass.name !== selectedClass.name)) {
        setSelectedClass(matchingClass);
      }
      return; // Don't override if class exists
    }

    // Only set fallback if stored class doesn't exist anymore
    if (selectedClass && !classStillExists) {
      const fallbackClass =
        classOptions[classOptions.length - 1] ?? classOptions[0];
      if (fallbackClass) {
        setSelectedClass(fallbackClass);
      } else {
        setSelectedClass(null);
      }
      return;
    }

    // Only set default if no class is selected AND we're not loading (to avoid overriding localStorage restore)
    if (!selectedClass && !classesQuery.isLoading) {
      const defaultClass =
        classOptions[classOptions.length - 1] ?? classOptions[0];
      if (defaultClass) {
        setSelectedClass(defaultClass);
      }
    }
  }, [classOptions, selectedClass, setSelectedClass, classesQuery.isLoading]);

  // Save selectedMedium to localStorage whenever it changes (only for school admin and teacher)
  // Only save if it's not "ALL" or if user explicitly selected a medium
  useEffect(() => {
    // For school admin and teacher, save to localStorage (but always default to "ALL" on init)
    // For students, save their medium to localStorage
    if (selectedMedium && (isSchoolAdmin() || isTeacher() || isStudent())) {
      try {
        localStorage.setItem('selectedMedium', selectedMedium);
      } catch (error) {
      }
    }
  }, [selectedMedium, isSchoolAdmin, isTeacher, isStudent]);

  // Ensure medium is set to "ALL" for school admin and teacher on mount/refresh/login
  useEffect(() => {
    // Only set default medium for school admin and teacher (not for students)
    // Students have their own logic that sets medium from student data
    if (!authLoading && user && (isSchoolAdmin() || isTeacher())) {
      // Always set to "ALL" on mount/refresh/login for school admin and teacher
      // This ensures it defaults to "ALL" even if localStorage has a different value
      // Only update if it's not already "ALL" to avoid unnecessary re-renders
      if (selectedMedium !== "ALL") {
        setSelectedMedium("ALL");
        // Clear any non-"ALL" value from localStorage for school admin/teacher
        try {
          localStorage.setItem('selectedMedium', "ALL");
        } catch (error) {
        }
      }
    }
  }, [authLoading, user?.id]); // Run when user loads or user ID changes (login/logout), not on every selectedMedium change

  // Clear localStorage when school changes
  useEffect(() => {
    // Clear previous selections when school changes
    if (schoolId) {
      // Only clear if we're switching to a different school
      const lastSchoolId = localStorage.getItem('lastSchoolId');
      if (lastSchoolId && lastSchoolId !== schoolId.toString()) {
        // Clear all storage and set new school ID
        clearAllStorage();
        localStorage.setItem('lastSchoolId', schoolId.toString());
      } else if (!lastSchoolId) {
        // First time setting school
        localStorage.setItem('lastSchoolId', schoolId.toString());
      }
    }
  }, [schoolId, clearAllStorage]);

  // Debug selectedMedium changes
  useEffect(() => {
  }, [selectedMedium]);

  // Set class, session, and medium from student data for students
  // Always update to match student's actual class and session (handles promotion updates)
  useEffect(() => {
    if (isStudent() && !authLoading && classesQuery.data && sessionsQuery.data && currentStudentData) {
      // Always set class from student's actual schoolClassId (from their record)
      if (currentStudentData.schoolClassId) {
        const studentClass = classesQuery.data?.find(
          (cls: any) => cls.id === currentStudentData.schoolClassId
        );
        if (studentClass) {
          // Always update to match student's actual class (handles promotion updates)
          const currentClassId = selectedClass?.id;
          if (currentClassId !== studentClass.id) {
            setSelectedClass(studentClass);
          }
        }
      }

      // Always set session from student's actual sessionId (from their record)
      if (currentStudentData.sessionId) {
        const studentSession = sessionsQuery.data?.find(
          (ses: any) => ses.id === currentStudentData.sessionId
        );
        if (studentSession) {
          // Always update to match student's actual session (handles promotion updates)
          const currentSessionId = selectedSession?.id;
          if (currentSessionId !== studentSession.id) {
            setSelectedSession(studentSession);
          }
        }
      }

      // Set medium from student data
      if (currentStudentData.medium) {
        // Always update to match student's actual medium
        if (selectedMedium !== currentStudentData.medium) {
          setSelectedMedium(currentStudentData.medium);
        }
      }
    }
  }, [
    isStudent,
    authLoading,
    currentStudentData,
    classesQuery.data,
    sessionsQuery.data,
    selectedClass,
    selectedSession,
    selectedMedium,
    setSelectedClass,
    setSelectedSession,
  ]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  // Fetch student data on mount if user is a student
  useEffect(() => {
    const fetchStudentName = async () => {
      if (isStudent() && user) {
        try {
          // Use currentStudentData from login
          if (currentStudentData) {
            // Use current student data directly for display name
            const completeStudentData = currentStudentData;
            if (completeStudentData) {
              const name = completeStudentData.studentName ||
                user?.username ||
                '';
              if (name) {
                setStudentName(name);
                return;
              }
            }
          }
          // Fallback to username if studentData is not loaded yet
          if (user?.username && !studentName) {
            setStudentName(user.username);
          }
        } catch (error) {
          // Fallback to username if available
          if (user?.username && !studentName) {
            setStudentName(user.username);
          }
        }
      }
    };

    if (!authLoading && user) {
      fetchStudentName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudent, user, authLoading, currentStudentData, currentStudentLoading]);

  // Handle Profile click for STUDENT - navigate to profile page
  const handleProfileClick = () => {
    handleProfileMenuClose();
    if (isStudent()) {
      navigate('/student/profile');
    }
  };


  const handleLogout = () => {
    handleProfileMenuClose();
    // Clear all localStorage using the context function
    clearAllStorage();
    logout();
    // Navigate to landing page after logout
    navigate('/');
  };

  // Get school mediums as array - filter for students to show only their medium
  const schoolMediums = isStudent() && studentData?.medium
    ? [studentData.medium]
    : (school?.medium
      ? (Array.isArray(school.medium) ? school.medium : [school.medium])
      : []);

  return (
    <MediumContext.Provider value={{
      selectedMedium,
      setSelectedMedium,
      schoolMediums
    }}>
      <Box sx={{ minHeight: "100vh" }}>
        <AppBar
          position="fixed"
          sx={{
            width: "100%",
            left: 0,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafe 100%)",
            color: "text.primary",
            borderBottom: "none",
            boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
            backdropFilter: "blur(10px)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar sx={{ pl: { xs: 1, sm: 2, md: 2 } }}>
            {!isAdmin() && (
              <>
                <IconButton
                  color="inherit"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    mr: 2,
                    fontSize: "1.5rem",
                    padding: "8px",
                    color: "#333",
                    "&:hover": {
                      transform: "scale(1.05)",
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transition: "all 0.2s ease-in-out"
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
                {/* Welcome message for students - right after hamburger icon */}
                {isStudent() && (studentName || user?.username) && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'black',
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      mr: 2,
                    }}
                  >
                    {t('welcome') || 'Welcome'}, {studentName || user?.username || ''}
                  </Typography>
                )}
              </>
            )}

            {isAdmin() && (
              <Box sx={{ padding: "12px 0", flex: 1 }}>
                <Typography
                  variant="h3"
                  fontWeight="bold"
                  sx={{
                    background: "#3092e3",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.5rem", md: "2rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {t("admin_dashboard")}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    fontWeight: 500,
                    opacity: 0.8
                  }}
                >
                  {t("multi_tenant_school_management_system")}
                </Typography>
              </Box>
            )}

            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              flex={1}
              width="100%"
              sx={{ ml: 'auto' }}
            >
              {!isAdmin() && (
                <>
                  {/* Right side - Dropdowns */}
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      flexShrink: 0,
                      ml: 'auto'
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 140,
                        mx: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease-in-out',
                          boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                          '& fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.2)',
                          },
                          '&:hover fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #3092e3',
                          },
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#3092e3',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: '#3092e3',
                          }
                        },
                        '& .MuiSelect-select': {
                          fontWeight: 500,
                          color: '#333',
                        },
                        '& .MuiSelect-select.Mui-selected': {
                          backgroundColor: 'rgba(50, 140, 249, 0.15)',
                          color: '#3092e3',
                          fontWeight: 600,
                        }
                      }}
                    >
                      <InputLabel id="class-select-label">{t("class")}</InputLabel>
                      <Select
                        labelId="class-select-label"
                        value={selectedClassValue}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          const classData = classOptions.find(
                            (c) => formatClassOption(c) === value
                          );
                          setSelectedClass(classData || null);
                        }}
                        label={t("class")}
                        disabled={classesQuery.isLoading || !schoolId || isStudent()}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 1,
                              boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                              border: '1px solid rgba(50, 140, 249, 0.1)',
                              maxHeight: 300,
                              '& .MuiMenuItem-root': {
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                  transform: 'translateX(4px)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                  color: '#3092e3',
                                  fontWeight: 600,
                                }
                              }
                            }
                          }
                        }}
                      >
                        {classesQuery.isLoading ? (
                          <MenuItem disabled>{t("loading")}</MenuItem>
                        ) : !classOptions.length ? (
                          <MenuItem disabled>{t("no_classes_available")}</MenuItem>
                        ) : (
                          classOptions.map((cls: any) => (
                            <MenuItem
                              key={`${cls.id ?? "x"}-${cls.name}-${cls.section ?? ""}`}
                              value={formatClassOption(cls)}
                            >
                              {formatClassOption(cls)}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 160,
                        mx: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease-in-out',
                          boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                          '& fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.2)',
                          },
                          '&:hover fieldset': {
                            border: '1px solid rgba(50, 140, 249, 0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            border: '1px solid #3092e3',
                          },
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                          }
                        },
                        '& .MuiInputLabel-root': {
                          color: '#3092e3',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: '#3092e3',
                          }
                        },
                        '& .MuiSelect-select': {
                          fontWeight: 500,
                          color: '#333',
                        }
                      }}
                    >
                      <InputLabel id="session-select-label">{t("session")}</InputLabel>
                      <Select
                        labelId="session-select-label"
                        value={selectedSessionValue}
                        onChange={(e) => {
                          const value = e.target.value as string;
                          const session = sessionData.find(
                            (s) => s.name === value
                          );
                          setSelectedSession(session || null);
                        }}
                        label={t("session")}
                        disabled={sessionsQuery.isLoading || !schoolId || isStudent()}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: 1,
                              boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                              border: '1px solid rgba(50, 140, 249, 0.1)',
                              maxHeight: 300,
                              '& .MuiMenuItem-root': {
                                borderRadius: 1,
                                mx: 1,
                                my: 0.5,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                  transform: 'translateX(4px)',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                  color: '#3092e3',
                                  fontWeight: 600,
                                }
                              }
                            }
                          }
                        }}
                      >
                        {sessionsQuery.isLoading ? (
                          <MenuItem disabled>{t("loading")}</MenuItem>
                        ) : sessionData.length === 0 ? (
                          <MenuItem disabled>{t("no_sessions_available")}</MenuItem>
                        ) : (
                          sessionData.map((session) => (
                            <MenuItem key={session.id ?? session.name} value={session.name}>
                              {session.name}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                    </FormControl>
                    {!isAppUser && (
                      <FormControl
                        size="small"
                        sx={{
                          minWidth: 140,
                          mx: 1,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease-in-out',
                            boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                            '& fieldset': {
                              border: '1px solid rgba(50, 140, 249, 0.2)',
                            },
                            '&:hover fieldset': {
                              border: '1px solid rgba(50, 140, 249, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                              border: '1px solid #3092e3',
                            },
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                            },
                            '&.Mui-focused': {
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                            }
                          },
                          '& .MuiInputLabel-root': {
                            color: '#3092e3',
                            fontWeight: 600,
                            '&.Mui-focused': {
                              color: '#3092e3',
                            }
                          },
                          '& .MuiSelect-select': {
                            fontWeight: 500,
                            color: '#333',
                          },
                          '& .MuiSelect-select.Mui-selected': {
                            backgroundColor: 'rgba(50, 140, 249, 0.15)',
                            color: '#3092e3',
                            fontWeight: 600,
                          }
                        }}
                      >
                        <InputLabel id="medium-select-label">{t("medium")}</InputLabel>
                        <Select
                          labelId="medium-select-label"
                          value={selectedMedium || ""}
                          displayEmpty
                          open={mediumDropdownOpen}
                          onOpen={() => {
                            setMediumDropdownOpen(true);
                          }}
                          onClose={() => {
                            setMediumDropdownOpen(false);
                          }}
                          onChange={(e) => {
                            const selectedValue = e.target.value as string;
                            setSelectedMedium(selectedValue);
                            setMediumDropdownOpen(false); // Close dropdown after selection
                          }}
                          renderValue={(value) => {
                            if (!value || value === "") {
                              return <em></em>;
                            }
                            return value;
                          }}
                          label={t("medium")}
                          disabled={schoolLoading || !schoolId || !school?.medium || isStudent()}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                borderRadius: 1,
                                boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                                border: '1px solid rgba(50, 140, 249, 0.1)',
                                maxHeight: 300,
                                '& .MuiMenuItem-root': {
                                  borderRadius: 1,
                                  mx: 1,
                                  my: 0.5,
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    backgroundColor: 'rgba(50, 140, 249, 0.08)',
                                    transform: 'translateX(4px)',
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(50, 140, 249, 0.15)',
                                    color: '#3092e3',
                                    fontWeight: 600,
                                  }
                                }
                              }
                            }
                          }}
                        >
                          {schoolLoading ? (
                            <MenuItem disabled>{t("loading")}</MenuItem>
                          ) : !school?.medium || (Array.isArray(school.medium) && school.medium.length === 0) ? (
                            <MenuItem disabled>{t("no_medium_available")}</MenuItem>
                          ) : (
                            <>
                              {Array.isArray(school?.medium) ? school.medium.map((medium) => (
                                <MenuItem
                                  key={medium}
                                  value={medium}
                                  onClick={() => {
                                    setSelectedMedium(medium);
                                    setMediumDropdownOpen(false);
                                  }}
                                >
                                  {medium}
                                </MenuItem>
                              )) : school?.medium ? (
                                <MenuItem
                                  value={typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                                  onClick={() => {
                                    const mediumValue = typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || '';
                                    setSelectedMedium(mediumValue);
                                    setMediumDropdownOpen(false);
                                  }}
                                >
                                  {typeof school?.medium === 'string' ? school.medium : (school?.medium && school.medium[0]) || ''}
                                </MenuItem>
                              ) : null}
                              <MenuItem
                                value="ALL"
                                onClick={() => {
                                  setSelectedMedium("ALL");
                                  setMediumDropdownOpen(false);
                                }}
                              >
                                <strong>ALL</strong>
                              </MenuItem>
                            </>
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </>
              )}

              {/* Right side - Language selector and User avatar */}
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  ml: isAdmin() ? 'auto' : 0,
                  flexShrink: 0
                }}
              >
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 150,
                    mx: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,248,255,0.9) 100%)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease-in-out',
                      boxShadow: '0 2px 8px rgba(50, 140, 249, 0.1)',
                      '& fieldset': {
                        border: '1px solid rgba(50, 140, 249, 0.2)',
                      },
                      '&:hover fieldset': {
                        border: '1px solid rgba(50, 140, 249, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        border: '1px solid #3092e3',
                      },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(50, 140, 249, 0.15)',
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(50, 140, 249, 0.2)',
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: '#3092e3',
                      fontWeight: 600,
                      '&.Mui-focused': {
                        color: '#3092e3',
                      }
                    },
                    '& .MuiSelect-select': {
                      fontWeight: 500,
                      color: '#333',
                    }
                  }}
                >
                  <InputLabel id="language-select-label"> {t("language")}</InputLabel>
                  <Select
                    labelId="language-select-label"
                    label={t("language")}
                    value={i18n.language}
                    onChange={(e) => {
                      const newLang = e.target.value;
                      i18n.changeLanguage(newLang);
                      // Persist language selection
                      try {
                        localStorage.setItem('selectedLanguage', newLang);
                        localStorage.setItem('i18nextLng', newLang);
                      } catch (error) {
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 1,
                          boxShadow: '0 8px 32px rgba(50, 140, 249, 0.15)',
                          border: '1px solid rgba(50, 140, 249, 0.1)',
                          '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(50, 140, 249, 0.08)',
                              transform: 'translateX(4px)',
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(50, 140, 249, 0.15)',
                              color: '#3092e3',
                              fontWeight: 600,
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="hi">हिन्दी</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>

                {user && (
                  <>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      size="small"
                      sx={{
                        ml: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          background: "#3092e3",
                          boxShadow: '0 4px 12px rgba(1, 104, 234, 0.3)',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleProfileMenuClose}
                      onClick={handleProfileMenuClose}
                      PaperProps={{
                        sx: {
                          borderRadius: 3,
                          boxShadow: '0 8px 32px rgba(59, 162, 245, 0.15)',
                          border: '1px solid rgba(59, 162, 245, 0.1)',
                          minWidth: 180,
                          '& .MuiMenuItem-root': {
                            borderRadius: 1,
                            mx: 1,
                            my: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(59, 162, 245, 0.08)',
                              transform: 'translateX(4px)',
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem onClick={isStudent() ? handleProfileClick : undefined}>
                        <ListItemIcon>
                          <AccountIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t("profile_management")}</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                            '& .MuiListItemText-primary': {
                              color: '#f44336',
                            }
                          }
                        }}
                      >
                        <ListItemText
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: '#f44336',
                              fontWeight: 500,
                            }
                          }}
                        >
                          {t("logout")}
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {!user?.roles.includes("ADMIN") && (
          <AppSideBar
            mobileOpen={mobileOpen}
            handleDrawerToggle={handleDrawerToggle}
            selectedClassId={selectedClass?.id}
            selectedSessionId={selectedSession?.id}
          />
        )}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            mt: "64px",
            ml: isAdmin()
              ? "0px"
              : {
                xs: "0px",
                md: mobileOpen ? `${DRAWER_WIDTH}px` : "0px"
              },
            width: isAdmin()
              ? "100%"
              : {
                xs: "100%",
                md: mobileOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%"
              },
            bgcolor: "#fafbfc",
            minHeight: "100vh",
            transition: "margin-left 0.3s ease-in-out, width 0.3s ease-in-out",
          }}
        >
          {user?.roles.includes("ADMIN") ? (
            <Box width="100%">
              <Outlet />
            </Box>
          ) : (
            <Container maxWidth="xl">
              <Outlet />
            </Container>
          )}
        </Box>

      </Box>
    </MediumContext.Provider>
  );
};

export default Layout;